import { Component, Signal, WritableSignal, computed, inject, input, linkedSignal, output } from '@angular/core';
import { AbstractControl, ControlValueAccessor, NgControl, ValidationErrors, Validators } from '@angular/forms';
import { IonItem, IonLabel, IonTextarea } from '@ionic/angular/standalone';
import type { IonTextareaCustomEvent, TextareaInputEventDetail } from '@ionic/core/components';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FORM_INPUT_VALIDATION_MESSAGES } from '@settings/form-validation-settings';
import { MbpFormErrorComponent } from '../mbp-form-error/mbp-form-error.component';

@Component({
  selector: 'mbp-textarea',
  templateUrl: './mbp-textarea.component.html',
  styleUrl: './mbp-textarea.component.scss',
  imports: [IonItem, IonLabel, IonTextarea, TranslateModule, MbpFormErrorComponent]
})
export class MbpTextareaComponent implements ControlValueAccessor {
  labelKey = input<string>('');
  placeholderKey = input<string>('');
  errorMessages = input<Record<string, string> | undefined>(undefined);
  layout = input<'row' | 'column'>('row');
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  rows = input<number>(4);
  maxCharacters = input<number | undefined>(undefined);
  value = input<string>('');
  trailingIconWidth = input<string | undefined>(undefined);

  valueChange = output<string>();
  inputFocus = output<void>();
  inputBlur = output<void>();

  isRequired: Signal<boolean> = computed(() => {
    this.internalValue();
    return this.required() || (this.control()?.hasValidator(Validators.required) ?? false);
  });

  errorMessage: Signal<string | undefined> = computed(() => this.getError());

  internalValue: WritableSignal<string> = linkedSignal<string>(() =>
    this.ngControl === undefined ? this.value() : ''
  );

  private onChange: (value: string) => void = () => undefined;
  private onTouched: () => void = () => undefined;
  private readonly translate = inject(TranslateService);
  private readonly ngControl: NgControl | undefined = inject(NgControl, { self: true, optional: true }) ?? undefined;

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  writeValue(value: string): void {
    this.internalValue.set(value ?? '');
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  protected handleInput(event: IonTextareaCustomEvent<TextareaInputEventDetail>): void {
    const nextValue = event.detail.value ?? '';
    this.internalValue.set(nextValue);
    this.onChange(nextValue);
    this.valueChange.emit(nextValue);
  }

  protected handleFocus(): void {
    this.inputFocus.emit();
  }

  protected handleBlur(): void {
    this.onTouched();
    this.inputBlur.emit();
  }

  private showError(): boolean {
    const control = this.control();
    if (!control) {
      return false;
    }
    return control.invalid && (control.dirty || control.touched);
  }

  private getError(): string | undefined {
    this.internalValue();

    if (!this.showError()) {
      return undefined;
    }

    const controlErrors: ValidationErrors | undefined = this.control()?.errors ?? undefined;
    if (!controlErrors) {
      return undefined;
    }

    for (const errorName of Object.keys(controlErrors)) {
      const mappedKey: string | undefined = this.resolveErrorKey(errorName);
      if (mappedKey) {
        const lengthValue = this.getRequiredLength(errorName);
        return this.translate.instant(mappedKey, lengthValue ? { count: lengthValue } : {});
      }
    }

    return this.translate.instant('form.validation.errors.invalid');
  }

  private resolveErrorKey(errorName: string): string | undefined {
    const specificKey: string | undefined = this.errorMessages()?.[errorName];
    if (specificKey) {
      return specificKey;
    }
    return FORM_INPUT_VALIDATION_MESSAGES[errorName];
  }

  private getRequiredLength(key: string): number | undefined {
    if (!['maxlength', 'minlength', 'maxLength', 'minLength'].includes(key)) {
      return undefined;
    }
    const lengthError: unknown = this.control()?.errors?.[key] ?? null;
    if (!lengthError || typeof lengthError !== 'object') {
      return undefined;
    }
    const errorRecord = lengthError as Record<string, unknown>;
    const requiredLength = errorRecord['requiredLength'] ?? errorRecord[key];
    return typeof requiredLength === 'number' ? requiredLength : undefined;
  }

  private control(): AbstractControl<unknown> | undefined {
    return (this.ngControl?.control as AbstractControl<unknown>) ?? undefined;
  }
}
